import React, {FC, ReactNode} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../../libs/shared/core/src/helpers/internationalization/react';
import {BoxedIcon} from '../../../../../libs/shared/ui/src/components/icon/boxedIcon';
import {ChildrenCell} from '../../../../../libs/shared/ui/src/components/tables/cells/childrenCell';
import {TableCellProps} from '../../../../../libs/shared/ui/src/components/tables/cells/tableCellTypes';
import {fontSizes, fontWeights} from '../../../../../libs/shared/ui/src/styles/fontStyles';
import {useUserTheme} from '../../../../../libs/shared/ui/src/theme/userThemeProvider';
import {isAdminOrMemberRoleAlias, RoleModel, SpecialRoleAliasesEnum} from '../../../../models/api/roleModel';

/*
 * Props.
 */

interface RoleCellProps extends TableCellProps<RoleModel | undefined> {
  roleAlias: string;
}

/*
 * Styles.
 */

const StyledRoleDiv = styled.div`
  display: flex;
  align-items: center;
`;

const StyledIconWrapperDiv = styled.div`
  margin: 0 12px;
`;

const StyledNameWrapperDiv = styled.div`
  display: flex;
  align-items: center;
`;

const StyledNameDiv = styled.div`
  font-weight: ${fontWeights.semibold};
`;

const StyledRoleSpecialDiv = styled.div`
  margin-left: 6px;
  padding: 1px 6px;
  border-radius: 99px;
  width: max-content;

  font-size: ${fontSizes.veryTiny};
  font-weight: ${fontWeights.bold};

  color: ${(p) => p.theme.greys.white};
  background-color: ${(p) => p.theme.greys.black};
  text-transform: uppercase;
`;

/*
 * Component.
 */

export const RoleCell: FC<RoleCellProps> = (props) => {
  const {value, roleAlias, ...childProps} = props;

  const theme = useUserTheme();

  const renderIcon = () => (
    <StyledIconWrapperDiv>
      <BoxedIcon
        name="teammateRole"
        color={theme.greys.shade70}
        borderColor={theme.greys.shade30}
        borderStyle="solid"
        size={props.size}
      />
    </StyledIconWrapperDiv>
  );

  const renderName = () => {
    const isSpecial = isAdminOrMemberRoleAlias(roleAlias);
    return (
      <StyledNameWrapperDiv>
        <StyledNameDiv>{renderRoleName(roleAlias, value)}</StyledNameDiv>
        {isSpecial && (
          <StyledRoleSpecialDiv>
            <Trans id="role-cell-default-role">Default</Trans>
          </StyledRoleSpecialDiv>
        )}
      </StyledNameWrapperDiv>
    );
  };

  return (
    // eslint-disable-next-line react/jsx-props-no-spreading
    <ChildrenCell {...childProps}>
      <StyledRoleDiv>
        {renderIcon()}
        {renderName()}
      </StyledRoleDiv>
    </ChildrenCell>
  );
};

/*
 * Helpers.
 */

function renderRoleName(alias: string, role: RoleModel | undefined): ReactNode {
  switch (alias) {
    case SpecialRoleAliasesEnum.ADMIN:
      return <Trans id="settings-role-table-admin-name">Workspace Admin</Trans>;

    case SpecialRoleAliasesEnum.MEMBER:
      return <Trans id="settings-role-table-member-name">Member</Trans>;

    default: {
      return role?.name ?? '';
    }
  }
}
